import React, { useState, useEffect } from 'react';
import { Tabs, Modal, notification } from 'antd';
import { Breadcrumbs } from 'components/Atom/breadcumbs';
import { ResetButton, Button } from 'components/Atom/Button';
import SearchField from 'components/Atom/SearchField/index';
import { PageHeader } from 'components/Atom/PageHeader';
import Table from 'components/Table';
import AddEntry from './addEntry';
import '../assets/style.css';
import { useSelector } from 'react-redux';
import { checkUserPermissions } from 'utils/services';
import { USER_ROLES } from 'utils/constant';
import { useQuery } from 'react-query';
import { TIMEZONES } from 'utils/constant';
import { formatInTimeZone } from 'date-fns-tz';
import { getDeclineList, deleteDeclineListArray, deleteDeclineListEntry } from 'api/xShields';
import { UploadOutlined, DeleteOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import ShortenId from 'components/Atom/ShortenId';
const TabPane = Tabs.TabPane;

export const BlockList = () => {
  const [open, setOpen] = useState(false);
  const store = useSelector((state) => state.company.store);
  const [activeTab, setActiveTab] = useState('1');
  const [search, setSearch] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [clearModal, setClearModal] = useState(false);
  const [rowData, setRowData] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [tabCounts, setTabCounts] = useState({});
  const [isDeclineArrayDeleting, setIsDeclineArrayDeleting] = useState(false);
  const [isDeclineEntryDeleting, setIsDeclineEntryDeleting] = useState(false);
  const userStoresData = useSelector((state) => state.company.userStores);

  const fetchData = async () => {
    try {
      const params = { ...(store?.isSelected ? { store_id: store?._id } : { store_id: userStoresData[0]?._id }) };
      const response = await getDeclineList(params);
      const timezone = store?.settings?.timezone || TIMEZONES?.PKT;
      const result = convertToStoreTimezone(response?.data?.data, timezone);
      return result;
    } catch (err) {
      console.log(err);
    }
  };
  const { data, refetch, isLoading, isFetching } = useQuery('decline_lists', () => fetchData(), {
    enabled: false,
    manual: true,
  });

  const convertToStoreTimezone = (data, timezone) => {
    if (!data?.decline_lists) return data;
    const convertCreatedAt = (list) =>
      list?.map((entry) => ({
        ...entry,
        createdAt: formatInTimeZone(entry?.createdAt, timezone, 'MMM d, yyyy h:mm:ss a'),
      })) || [];
    return {
      ...data,
      decline_lists: {
        ...data?.decline_lists,
        bins: convertCreatedAt(data?.decline_lists?.bins),
        emails: convertCreatedAt(data?.decline_lists?.emails),
        phone_numbers: convertCreatedAt(data?.decline_lists?.phone_numbers),
        ip_addresses: convertCreatedAt(data?.decline_lists?.ip_addresses),
        card_fingerprints: convertCreatedAt(data?.decline_lists?.card_fingerprints),
      },
    };
  };

  useEffect(() => {
    if (store?.features?.includes('XShield')) {
      refetch();
    }
  }, [store]);

  useEffect(() => {
    setSearch('');
    if (data?.decline_lists) {
      const { bins = [], emails = [], phone_numbers = [], ip_addresses = [], card_fingerprints = [] } = data?.decline_lists || {};
      const counts = {
        bins: bins?.length,
        emails: emails?.length,
        phone_numbers: phone_numbers?.length,
        ip_addresses: ip_addresses?.length,
        card_fingerprints: card_fingerprints?.length,
      };
      setTabCounts(counts);
      const activeKey = tabTypes[activeTab];
      setFilteredData(data?.decline_lists?.[activeKey] || []);
    } else {
      setFilteredData([]);
      setTabCounts({
        bins: 0,
        emails: 0,
        phone_numbers: 0,
        ip_addresses: 0,
        card_fingerprints: 0,
      });
    }
  }, [data, activeTab, store]);

  const tabMappings = {
    1: 'BIN',
    2: 'Email Address',
    3: 'Phone',
    4: 'IP Address',
    5: 'Card Fingerprint',
  };

  const tabTypes = {
    1: 'bins',
    2: 'emails',
    3: 'phone_numbers',
    4: 'ip_addresses',
    5: 'card_fingerprints',
  };

  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'XShield',
      },
      {
        name: 'Block List',
      },
    ],
  };
  const columnsData = [
    {
      title: 'Value',
      render: (value) => (value?.value?.length > 30 ? <ShortenId value={value?.value} /> : value?.value),
      key: 'value',
    },
    {
      title: 'Created on',
      render: (value) => value?.createdAt,
      key: 'createdAt',
    },
    ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN])
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (data, index) => {
              return (
                <div key={index} className='flex items-center gap-2'>
                  <>
                    <div
                      type='primary'
                      className='table-action-btn'
                      onClick={() => {
                        setDeleteModal(true), setRowData(data);
                      }}
                      style={{ cursor: 'pointer' }}>
                      Delete
                    </div>
                  </>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const handleDeleteDeclineEntry = async (decline_list_type, value) => {
    setIsDeclineEntryDeleting(true);
    const response = await deleteDeclineListEntry(store?._id, decline_list_type, value);
    if (response?.data?.success) {
      setDeleteModal(false);
      refetch();
    }
    setIsDeclineEntryDeleting(false);
  };

  const handleDeleteDeclineArray = async (decline_list_type) => {
    setIsDeclineArrayDeleting(true);
    const response = await deleteDeclineListArray(store?._id, decline_list_type);
    if (response?.data?.success) {
      setClearModal(false);
      refetch();
    }
    setIsDeclineArrayDeleting(false);
  };
  const onTabChange = (key) => {
    setActiveTab(key);
  };
  const handleOpen = () => {
    setOpen(!open);
  };

  const handleSearch = () => {
    if (!data?.decline_lists) return;
    const activeKey = tabTypes[activeTab];
    const fullData = data?.decline_lists?.[activeKey] || [];
    const filtered = fullData?.filter((item) => item?.value?.toLowerCase()?.includes(search?.toLowerCase()));
    setFilteredData(filtered);
  };
  const handleSearchData = () => {
    setSearch('');
    setFilteredData(data?.decline_lists?.[tabTypes[activeTab]] || []);
  };

  const handleFileSelectionModel = (isClear) => {
    if (store?.isSelected) {
      if (isClear) {
        setClearModal(true);
      } else {
        setOpen(true);
      }
    } else {
      notification.error({
        message: 'Store Selection Required',
        description: 'Please select a store from the drop-down menu above to continue',
        placement: 'bottomRight',
      });
    }
  };
  return (
    <>
      <div className='xshield' style={{ padding: '0px', width: '100%' }}>
        <div className='header-container'>
          <Breadcrumbs {...breadcrumbProps} />
          <div className='headerPage'>
            <div className=''>
              <PageHeader label={'Block List'} button={<ResetButton onClick={() => refetch()} loading={isFetching} />} />
            </div>
          </div>
        </div>
        <div className='store-info-container'>
          <Tabs defaultActiveKey='1' onTabClick={onTabChange} style={{ width: '100%' }}>
            {Object.keys(tabMappings)?.map((key) => (
              <TabPane key={key} tab={`${tabMappings[key]} (${tabCounts[tabTypes[key]] || 0})`} />
            ))}
          </Tabs>
        </div>
        <div className='action_Panel headerContainer' style={{ width: '100%', marginTop: '12px', marginBottom: '24px' }}>
          <div className='searchInput'>
            <SearchField
              handleOnChange={(e) => setSearch(e?.target?.value)}
              value={search}
              handlePressEnter={handleSearch}
              onReset={handleSearchData}
              placeholder={`Search ${tabMappings[activeTab]}`}
              loading={!!(isFetching && search)}
              dropdown={{ show: false }}
              enableSearchBtn={true}
              style={{ width: '360px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px' }} className='action-buttons'>
            {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
              <Button
                disabled={''}
                className='custom-btns primaryBtn p-2 w-[120px] flex items-center'
                onClick={() => {
                  handleFileSelectionModel(false);
                }}>
                <PlusOutlined className='inline text-base' /> <span className='text-base'>Add Entry</span>
              </Button>
            )}
            {/*   <Button disabled={''} className='custom-btns export p-2 w-[120px] flex items-center bg-white' onClick={''}>
              <UploadOutlined className='inline text-base' /> <span className='text-base'>Export</span>
            </Button>
            <Button disabled={''} className='custom-btns import p-2 w-[120px] flex items-center bg-white' onClick={''}>
              <DownloadOutlined className='inline text-base' /> <span className='text-base'>Import</span>
            </Button> */}
            {/* {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN]) && (
              <Button
                disabled={''}
                className='custom-btns clear p-2 w-[120px] flex items-center'
                onClick={() => {
                  handleFileSelectionModel(true);
                }}>
                <DeleteOutlined className='inline text-base' /> <span className='text-base'>Delete All</span>
              </Button>
            )} */}
          </div>
        </div>
        <div className='overflow-x-auto custom-table'>
          <Table
            loading={isLoading}
            dataSource={filteredData?.length > 0 ? filteredData : []}
            columns={columnsData}
            locale={{ emptyText: 'No data' }}
            scroll={{ x: 'auto' }}
            totalLenght={filteredData?.length}
            pagingation={{ perPageSize: 20, page: 1 }}
          />
        </div>
      </div>
      <Modal
        open={deleteModal}
        centered
        width={400}
        onCancel={() => {
          setDeleteModal(false);
        }}
        footer={false}
        okButtonProps={{ loading: isDeclineEntryDeleting }}
        destroyOnClose>
        <header className='font-semibold text-base mb-4'>Delete {tabMappings[activeTab]}</header>
        <div className='mb-8'>Are you sure you want to delete this {rowData?.value}?</div>
        <div className='flex mt-4 popupConfirmBtn'>
          <Button
            onClick={() => {
              setDeleteModal(false);
            }}
            block
            className='rounded-lg bg-plain'>
            Cancel
          </Button>
          <Button className='DeleteBtn ml-4 rounded-lg' block onClick={() => handleDeleteDeclineEntry(tabTypes[activeTab], rowData?.value)} loading={isDeclineEntryDeleting}>
            Confirm
          </Button>
        </div>
      </Modal>
      <Modal
        open={clearModal}
        centered
        width={400}
        onCancel={() => {
          setClearModal(false);
        }}
        footer={false}
        okButtonProps={{ loading: isDeclineArrayDeleting }}
        destroyOnClose>
        <header className='font-semibold text-base mb-4'>Clearing the Block List for {tabMappings[activeTab]}</header>
        <div className='mb-4'>This will permanently remove all entries from the Block List for {tabMappings[activeTab]} and might take a while for lists with many entries.</div>
        <div className='mb-4'>Are you sure you want to proceed?</div>
        <div className='flex mt-4 popupConfirmBtn'>
          <Button
            onClick={() => {
              setClearModal(false);
            }}
            block
            className='rounded-lg bg-plain'>
            Cancel
          </Button>
          <Button className='DeleteBtn ml-4 rounded-lg' block onClick={() => handleDeleteDeclineArray(tabTypes[activeTab])} loading={isDeclineArrayDeleting}>
            Confirm
          </Button>
        </div>
      </Modal>
      <Modal trigger='click' centered open={open} width={window.innerWidth < 768 ? 350 : 400} footer={null} destroyOnClose onCancel={handleOpen}>
        <div className='mobileVersion'>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            {'Add new entry'}
          </div>
          <AddEntry handleOpen={handleOpen} refetch={refetch} tabTypes={tabTypes} activeTab={activeTab} tabMappings={tabMappings} />
        </div>
      </Modal>
    </>
  );
};
